import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SpotlightRenderer } from "../spotlight/SpotlightRenderer";
import {
  scrollTargetIntoView,
  targetNeedsScrollIntoView,
} from "../spotlight/targetVisibility";
import { findTargetElement } from "../spotlight/targets";
import { useTargetRect } from "../spotlight/useTargetRect";
import { placeCard } from "../spotlight/positioning";
import { CoachingCard, WelcomeModal } from "../ui/CoachingCard";
import { ensureMentorStylesInjected } from "../ui/mentorStyles";
import { useReducedMotion } from "../ui/useReducedMotion";
import type {
  DashboardTourStepDefinition,
  DashboardTourContextData,
} from "./types";

const CARD_MIN_WIDTH = 320;
const CARD_EST_HEIGHT = 160;
const TARGET_SCROLL_MARGIN_TOP = 96;
const TARGET_SCROLL_MARGIN_BOTTOM = 32;
const TARGET_SCROLL_RETRY_DELAYS_MS = [0, 120, 260, 520, 900, 1400, 2000];

interface DashboardTourOverlayProps {
  step: DashboardTourStepDefinition;
  ctx: DashboardTourContextData;
  stepLabel?: string;
  onAdvance: () => void;
  onDismissTour: () => void;
  onSkipTour: () => void;
}

function resolveTarget(
  step: DashboardTourStepDefinition,
  ctx: DashboardTourContextData,
) {
  return typeof step.target === "function" ? step.target(ctx) : step.target;
}

export function DashboardTourOverlay({
  step,
  ctx,
  stepLabel,
  onAdvance,
  onDismissTour,
  onSkipTour,
}: DashboardTourOverlayProps) {
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    ensureMentorStylesInjected();
  }, []);

  const targetId = resolveTarget(step, ctx);
  const rectInfo = useTargetRect(targetId, true);
  const useModal =
    step.placement === "modal-center" ||
    (targetId !== null && rectInfo.status === "not_found");

  const autoPassedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!step.autoPass) return;
    if (autoPassedRef.current === step.id) return;
    if (!step.autoPass(ctx)) return;
    autoPassedRef.current = step.id;
    onAdvance();
  }, [ctx, onAdvance, step]);

  useEffect(() => {
    autoPassedRef.current = null;
  }, [step.id]);

  const scrolledTargetRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      !targetId ||
      useModal ||
      rectInfo.status !== "ready" ||
      !rectInfo.element
    ) {
      return;
    }

    if (scrolledTargetRef.current === step.id) return;
    scrolledTargetRef.current = step.id;

    const timeouts = TARGET_SCROLL_RETRY_DELAYS_MS.map((delay, index) =>
      window.setTimeout(() => {
        const targetElement = findTargetElement(targetId);
        if (!targetElement) return;
        if (!document.contains(targetElement)) return;
        if (
          !targetNeedsScrollIntoView(targetElement, {
            viewportTopMargin: TARGET_SCROLL_MARGIN_TOP,
            viewportBottomMargin: TARGET_SCROLL_MARGIN_BOTTOM,
          })
        ) {
          return;
        }
        scrollTargetIntoView(targetElement, {
          viewportTopMargin: TARGET_SCROLL_MARGIN_TOP,
          viewportBottomMargin: TARGET_SCROLL_MARGIN_BOTTOM,
          behavior: reducedMotion || index > 0 ? "auto" : "smooth",
        });
      }, delay),
    );

    return () => {
      for (const timeout of timeouts) {
        window.clearTimeout(timeout);
      }
    };
  }, [
    rectInfo.element,
    rectInfo.status,
    reducedMotion,
    step.id,
    targetId,
    useModal,
  ]);

  const portalNode = useMemo(() => {
    if (typeof document === "undefined") return null;
    return document.body;
  }, []);

  const copy = useMemo(() => step.copy(ctx), [ctx, step]);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [cardSize, setCardSize] = useState({
    w: CARD_MIN_WIDTH,
    h: CARD_EST_HEIGHT,
  });
  useLayoutEffect(() => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    if (
      Math.abs(rect.width - cardSize.w) > 1 ||
      Math.abs(rect.height - cardSize.h) > 1
    ) {
      setCardSize({ w: rect.width, h: rect.height });
    }
  }, [cardSize.h, cardSize.w, step.id]);

  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1280,
    h: typeof window !== "undefined" ? window.innerHeight : 720,
  }));
  useEffect(() => {
    const onResize = () => {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  function handlePrimary() {
    if (step.primaryAction) {
      void step.primaryAction(ctx);
      return;
    }
    onAdvance();
  }

  function handleSecondary() {
    if (step.secondaryAction) {
      void step.secondaryAction(ctx);
      return;
    }
    if (step.tier === "soft") {
      onAdvance();
      return;
    }
    onSkipTour();
  }

  if (!portalNode) return null;

  const secondaryLabel =
    step.showDefaultSecondary === false
      ? copy.secondaryLabel
      : (copy.secondaryLabel ??
        (step.tier === "soft" ? "Skip step" : "Skip walkthrough"));

  const announcement = stepLabel ? `${stepLabel}: ${copy.title}` : copy.title;
  const announcer = createPortal(
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="staylayer-mentor-sr-only"
    >
      {announcement}
    </div>,
    portalNode,
  );

  if (useModal) {
    return (
      <>
        {announcer}
        {createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 23, 42, 0.55)",
              zIndex: 9998,
            }}
          >
            <WelcomeModal
              title={copy.title}
              body={
                <>
                  {copy.body}
                  {copy.hint ? (
                    <div className="mt-2 text-xs leading-relaxed text-slate-400">
                      {copy.hint}
                    </div>
                  ) : null}
                </>
              }
              stepLabel="Dashboard Tour"
              primaryLabel={copy.primaryLabel}
              onPrimary={handlePrimary}
              secondaryLabel={secondaryLabel}
              onSecondary={secondaryLabel ? handleSecondary : undefined}
              onDismiss={onDismissTour}
            />
          </div>,
          portalNode,
        )}
      </>
    );
  }

  const rect = rectInfo.rect;
  const placement = rect
    ? placeCard({
        target: rect,
        card: cardSize,
        viewport,
        preference: step.placement,
      })
    : null;

  return (
    <>
      {announcer}
      {createPortal(
        <SpotlightRenderer
          rect={rect}
          card={
            placement && rect ? (
              <div
                ref={cardRef}
                style={{
                  position: "absolute",
                  left: placement.left,
                  top: placement.top,
                  pointerEvents: "auto",
                }}
              >
                <CoachingCard
                  title={copy.title}
                  body={copy.body}
                  hint={copy.hint}
                  stepLabel={stepLabel}
                  primaryLabel={copy.primaryLabel}
                  onPrimary={handlePrimary}
                  secondaryLabel={secondaryLabel}
                  onSecondary={secondaryLabel ? handleSecondary : undefined}
                  onDismiss={onDismissTour}
                />
              </div>
            ) : null
          }
        />,
        portalNode,
      )}
    </>
  );
}
